export const EXCEPTION_TEST_FIELD_LABELS = ['Campos de teste', 'Mais outro teste testado'];
export const EXTRA_ATTACHMENTS_FIELD_NAME = 'DownloadDealExtraAttachments';

const OFFICIAL2_FIELD_NAME = 'DownloadDealBuyOrderOfficial2';

const AGENDA_SCHEDULING_NAME = 'Agenda Agendamento';
const SCHEDULE_SCHEDULING_NAME = 'ScheduleScheduling';

export function toPascalCase(label) {
  const stripped = label
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return stripped
    .split(/[^A-Za-z0-9]+/)
    .filter((token) => token.length > 0)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join('');
}

export function generatedExceptionFieldName(label) {
  return toPascalCase(label);
}

export function isExistingResourceField(field) {
  if (!field) {
    return false;
  }

  const isNew = Boolean(field.isNew ?? field.IsNew);
  const fieldType = field.fieldType ?? field.FieldType;
  return !isNew && !fieldType;
}

export function isRoleFieldEnabled(permission) {
  if (!permission) {
    return false;
  }

  const access = Number(permission.access ?? permission.Access ?? 0);
  const isRequired = Boolean(permission.isRequired ?? permission.IsRequired);
  return access > 0 || isRequired;
}

export function roleFieldPermissionsByName(role, resourceId) {
  const resources = role?.resources ?? role?.Resources ?? [];
  const numericId = Number(resourceId);
  const resource = resources.find((item) => Number(item.id ?? item.Id) === numericId);
  const permissions = {};
  (resource?.fields ?? resource?.Fields ?? []).forEach((field) => {
    const name = field.name ?? field.Name;
    if (!name) {
      return;
    }

    permissions[name] = {
      access: Number(field.access ?? field.Access ?? 0),
      isRequired: Boolean(field.isRequired ?? field.IsRequired),
    };
  });
  return permissions;
}

export function applyRoleFieldPermissions(fields, sections, permissionsByName) {
  const enabledNames = new Set(
    Object.entries(permissionsByName ?? {})
      .filter(([, permission]) => isRoleFieldEnabled(permission))
      .map(([name]) => name)
  );
  const nextFields = (fields ?? []).filter((field) => enabledNames.has(field.name ?? field.Name));
  const allowed = new Set(nextFields.map((field) => field.name ?? field.Name));
  const nextSections = (sections ?? []).map((section) => ({
    ...section,
    fieldNames: (section.fieldNames ?? section.FieldNames ?? []).filter((name) =>
      allowed.has(name)
    ),
  }));
  return { fields: nextFields, sections: nextSections };
}

export function existingFieldOptions(fields) {
  return (fields ?? []).filter(isExistingResourceField).map((field) => ({
    label: field.label ?? field.Label ?? field.name ?? field.Name,
    value: field.name ?? field.Name,
  }));
}

function createdCustomizationFieldNames(fields) {
  return (fields ?? [])
    .filter((field) => !isExistingResourceField(field))
    .map((field) => field.name ?? field.Name)
    .filter(Boolean);
}

function copySectionWithoutCreatedNames(section, createdNames) {
  const fieldNames = section.fieldNames ?? section.FieldNames ?? [];
  return {
    ...section,
    fieldNames: fieldNames.filter((name) => !createdNames.has(name)),
  };
}

export function stripCustomizationCreatedFieldsFromSections(sections, fields) {
  const createdNames = new Set(createdCustomizationFieldNames(fields));
  return (sections ?? []).map((section) => copySectionWithoutCreatedNames(section, createdNames));
}

export function removeSection(sections, sectionId) {
  return (sections ?? []).filter(
    (section) => (section.sectionId ?? section.SectionId) !== sectionId
  );
}

export function isAgendaSchedulingResource(resourceName) {
  if (resourceName == null || resourceName === '') {
    return false;
  }

  const name = String(resourceName).trim();
  return name === AGENDA_SCHEDULING_NAME || name === SCHEDULE_SCHEDULING_NAME;
}

function labelMatchesExceptionTest(label) {
  if (!label) {
    return false;
  }

  const trimmed = label.trim().toLowerCase();
  return EXCEPTION_TEST_FIELD_LABELS.some((expected) => expected.toLowerCase() === trimmed);
}

function nameMatchesExceptionTest(name) {
  if (!name) {
    return false;
  }

  return EXCEPTION_TEST_FIELD_LABELS.some((label) => name === generatedExceptionFieldName(label));
}

export function isExceptionTestField(field) {
  if (!field) {
    return false;
  }

  const label = field.label ?? field.Label;
  const name = field.name ?? field.Name;
  return labelMatchesExceptionTest(label) || nameMatchesExceptionTest(name);
}

export function stripExceptionTestFields(fields, resourceName) {
  if (!isAgendaSchedulingResource(resourceName)) {
    return fields;
  }

  return (fields ?? []).filter((field) => !isExceptionTestField(field));
}

function extraAttachmentsCustomizationField(order) {
  return {
    key: EXTRA_ATTACHMENTS_FIELD_NAME,
    name: EXTRA_ATTACHMENTS_FIELD_NAME,
    label: 'Anexos Extras',
    order,
    visibilityMode: 'Unrestricted',
    viewerRoleIds: [],
    isNew: false,
    fieldType: null,
  };
}

function fieldNamesWithoutExtraAttachments(fieldNames) {
  return (fieldNames ?? []).filter((fieldName) => fieldName !== EXTRA_ATTACHMENTS_FIELD_NAME);
}

export function ensureExtraAttachmentsInCustomization(fields, sections) {
  const withoutExtra = (fields ?? []).filter(
    (field) => field.name !== EXTRA_ATTACHMENTS_FIELD_NAME
  );
  const officialIndex = withoutExtra.findIndex((field) => field.name === OFFICIAL2_FIELD_NAME);
  if (officialIndex < 0) {
    return { fields: fields ?? [], sections: sections ?? [] };
  }

  const nextFields = withoutExtra.slice();
  const officialOrder = withoutExtra[officialIndex].order ?? officialIndex;
  nextFields.splice(officialIndex + 1, 0, extraAttachmentsCustomizationField(officialOrder + 1));

  const nextSections = (sections ?? []).map((section) => {
    const names = fieldNamesWithoutExtraAttachments(section.fieldNames);
    const anchor = names.indexOf(OFFICIAL2_FIELD_NAME);
    if (anchor < 0) {
      if ((section.fieldNames ?? []).includes(EXTRA_ATTACHMENTS_FIELD_NAME)) {
        return { ...section, fieldNames: names };
      }
      return section;
    }

    names.splice(anchor + 1, 0, EXTRA_ATTACHMENTS_FIELD_NAME);
    return { ...section, fieldNames: names };
  });

  return { fields: nextFields, sections: nextSections };
}

export function stripExceptionTestNamesFromSections(sections, names) {
  const nameSet = new Set(names ?? []);
  return (sections ?? []).map((section) => ({
    ...section,
    fieldNames: (section.fieldNames ?? section.FieldNames ?? []).filter(
      (name) => !nameSet.has(name)
    ),
  }));
}
